import type { TabInstance } from '../hooks/useTabs';

export interface TabBarProps {
  tabs: TabInstance[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onClose?: (id: string) => void;
  /** "top" = fanelinjen i topbaren, "bottom" = vognfaner i et panel. */
  variant?: 'top' | 'bottom';
  className?: string;
  ariaLabel?: string;
}

/**
 * Fanelinje. Bruges baade i topbaren (aabne sider) og i paneler (vognfaner),
 * som i referencen `HBPlanner v2.html` (`nav-tab` og `vtab`).
 */
export function TabBar({
  tabs,
  activeId,
  onSelect,
  onClose,
  variant = 'top',
  className,
  ariaLabel = 'Åbne faner',
}: TabBarProps) {
  return (
    <div
      className={`tab-bar ${variant}${className ? ` ${className}` : ''}`}
      role="tablist"
      aria-label={ariaLabel}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeId;
        const closable = tab.closable !== false && onClose;
        return (
          <div
            key={tab.id}
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            className={`app-tab${isActive ? ' active' : ''}`}
            onClick={() => onSelect(tab.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelect(tab.id);
              }
            }}
          >
            <span className="app-tab-label">{tab.label}</span>
            {closable && (
              <button
                type="button"
                className="app-tab-x"
                aria-label={`Luk ${tab.label}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onClose(tab.id);
                }}
              >
                ✕
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
